import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const API_KEY_STORAGE_KEY = 'externalIntegration_apiKey';

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401) return 'Integration not authorized. Please confirm your API key.';
  if (status === 404) return 'Course not found.';
  if (status === 500) return 'Server error. Please try again shortly.';
  return error?.response?.data?.error || error?.message || 'Unable to load lessons right now.';
};

const ExternalCourseLessonsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [apiKeyInput, setApiKeyInput] = useState(() => localStorage.getItem(API_KEY_STORAGE_KEY) || '');
  const [botLinkedOnly, setBotLinkedOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);

  const canLoad = useMemo(() => Boolean(courseId && apiKeyInput.trim()), [courseId, apiKeyInput]);

  const fetchLessons = async () => {
    if (!canLoad) return;

    setLoading(true);
    setError('');
    try {
      localStorage.setItem(API_KEY_STORAGE_KEY, apiKeyInput.trim());
      const response = await api.get(`/public/courses/${courseId}/lessons`, {
        headers: {
          'x-api-key': apiKeyInput.trim()
        },
        params: {
          botLinkedOnly: botLinkedOnly ? 'true' : 'false'
        }
      });

      setCourse(response.data?.course || null);
      setLessons(Array.isArray(response.data?.lessons) ? response.data.lessons : []);
    } catch (err) {
      setCourse(null);
      setLessons([]);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h1 className="text-2xl font-bold text-gray-900">Course Lessons (External Integration)</h1>
          <p className="text-sm text-gray-600 mt-1">Load published lessons and identify bot-linked lessons.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
            <div className="md:col-span-1">
              <label htmlFor="external-course-id" className="block text-sm font-medium text-gray-700 mb-1">Course ID</label>
              <input
                id="external-course-id"
                value={courseId || ''}
                onChange={(e) => navigate(`/external/courses/${e.target.value || '1'}/lessons`)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. 1"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="external-course-api-key" className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <input
                id="external-course-api-key"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter external integration API key"
                type="password"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={botLinkedOnly}
                onChange={(e) => setBotLinkedOnly(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              Bot-linked only
            </label>
            <button
              onClick={fetchLessons}
              disabled={!canLoad || loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {loading ? 'Loading...' : 'Load Lessons'}
            </button>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}
        </div>

        {course && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-sm text-blue-700">Course</p>
            <h2 className="text-lg font-semibold text-blue-900">{course.title || `Course #${course.id}`}</h2>
          </div>
        )}

        {!loading && !error && course && lessons.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center">
            <p className="text-gray-600">No lessons found for this course and filter.</p>
          </div>
        )}

        {lessons.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Lessons</h3>
              <span className="text-sm text-gray-500">{lessons.length} found</span>
            </div>
            <div className="divide-y divide-gray-100">
              {lessons.map((lesson) => (
                <div key={lesson.id} className="p-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{lesson.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{lesson.description || 'No description provided.'}</p>
                    <div className="mt-2 flex items-center gap-2">
                      {lesson.addPastQuestions ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Past Questions Available
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Not linked
                        </span>
                      )}
                      {lesson.topicHint && (
                        <span className="text-xs text-gray-500">Topic hint: {lesson.topicHint}</span>
                      )}
                    </div>
                  </div>

                  <Link
                    to={`/external/lessons/${lesson.id}/past-questions`}
                    className="inline-flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium"
                  >
                    Find Questions & Ask Bot
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalCourseLessonsPage;
